import {
  Button,
  Checkbox,
  cn,
  DragHandle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Trash,
} from "@gearbox-protocol/deploy-tools-ui";
import type { FC, SVGProps } from "react";
import { useCallback } from "react";
import type { Control, UseFieldArrayRemove } from "react-hook-form";
import { useFormContext, useWatch } from "react-hook-form";
import { useToggle } from "react-use";

import type { ForkSchema } from "../schema";
import {
  DEFAULT_DOCKER_IMAGE,
  DEFAULT_NPX_ARGS,
  DEFAULT_NPX_PACKAGE,
  FLAG_IGNORE_ERRORS,
  FLAG_SYNC_FEEDS,
} from "./defaults";
import ScriptArgsLite from "./ScriptArgsLite";
import ScriptDockerLite from "./ScriptDockerLite";
import ScriptEnv from "./ScriptEnv";
import ScriptNpxLite from "./ScriptNpxLite";

interface ScriptControlLiteProps {
  control: Control<ForkSchema, any>;
  name: "scripts";
  index: number;
  onStartDrag: (e: React.PointerEvent) => void;
  removeScript: UseFieldArrayRemove;
}

type ExecutorMode = "npm" | "docker";

interface ScriptFlagOption {
  value: string;
  label: string;
}

const SCRIPT_FLAGS: ScriptFlagOption[] = [
  { value: FLAG_SYNC_FEEDS, label: "Sync feeds" },
  { value: FLAG_IGNORE_ERRORS, label: "Ignore errors" },
];

const NpmIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M1.763 0C.786 0 0 .786 0 1.763v20.474C0 23.214.786 24 1.763 24h20.474c.977 0 1.763-.786 1.763-1.763V1.763C24 .786 23.214 0 22.237 0zM5.13 5.323l13.837.019-.009 13.836h-3.464l.01-10.382h-3.456L12.04 19.17H5.113z" />
  </svg>
);

const DockerIcon: FC<SVGProps<SVGSVGElement>> = props => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M13.983 11.078h2.119a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.119a.185.185 0 00-.185.185v1.888c0 .102.083.185.185.185m-2.954-5.43h2.118a.186.186 0 00.186-.186V3.574a.186.186 0 00-.186-.185h-2.118a.185.185 0 00-.185.185v1.888c0 .102.082.185.185.185m0 2.716h2.118a.187.187 0 00.186-.186V6.29a.186.186 0 00-.186-.185h-2.118a.185.185 0 00-.185.185v1.887c0 .102.082.185.185.186m-2.93 0h2.12a.186.186 0 00.184-.186V6.29a.185.185 0 00-.185-.185H8.1a.185.185 0 00-.185.185v1.887c0 .102.083.185.185.186m-2.964 0h2.119a.186.186 0 00.185-.186V6.29a.185.185 0 00-.185-.185H5.136a.186.186 0 00-.186.185v1.887c0 .102.084.185.186.186m5.893 2.715h2.118a.186.186 0 00.186-.185V9.006a.186.186 0 00-.186-.186h-2.118a.185.185 0 00-.185.185v1.888c0 .102.082.185.185.185m-2.93 0h2.12a.185.185 0 00.184-.185V9.006a.185.185 0 00-.184-.186h-2.12a.185.185 0 00-.184.185v1.888c0 .102.083.185.185.185m-2.964 0h2.119a.185.185 0 00.185-.185V9.006a.185.185 0 00-.184-.186h-2.12a.186.186 0 00-.186.186v1.887c0 .102.084.185.186.185m-2.92 0h2.12a.185.185 0 00.184-.185V9.006a.185.185 0 00-.184-.186h-2.12a.185.185 0 00-.184.185v1.888c0 .102.082.185.185.185M23.763 9.89c-.065-.051-.672-.51-1.954-.51-.338.001-.676.03-1.01.087-.248-1.7-1.653-2.53-1.716-2.566l-.344-.199-.226.327c-.284.438-.49.922-.612 1.43-.23.97-.09 1.882.403 2.661-.595.332-1.55.413-1.744.42H.751a.751.751 0 00-.75.748 11.376 11.376 0 00.692 4.062c.545 1.428 1.355 2.48 2.41 3.124 1.18.723 3.1 1.137 5.275 1.137.983.003 1.963-.086 2.93-.266a12.248 12.248 0 003.823-1.389c.98-.567 1.86-1.288 2.61-2.136 1.252-1.418 1.998-2.997 2.553-4.4h.221c1.372 0 2.215-.549 2.68-1.009.309-.293.55-.65.707-1.046l.098-.288z" />
  </svg>
);

const ScriptControlLite: FC<ScriptControlLiteProps> = props => {
  const { control, name, index, onStartDrag, removeScript } = props;
  const [isExpanded, toggleExpanded] = useToggle(false);

  const { setValue } = useFormContext<ForkSchema>();
  const docker = useWatch({ control, name: `${name}.${index}.docker` });
  const flags = useWatch({ control, name: `${name}.${index}.flags` });
  const mode: ExecutorMode = docker != null ? "docker" : "npm";

  const toggleFlag = useCallback(
    (flag: string, checked: boolean) => {
      const current = flags ?? [];
      const next = checked
        ? [...current, flag]
        : current.filter(f => f !== flag);
      setValue(`${name}.${index}.flags`, next.length ? next : undefined);
    },
    [setValue, name, index, flags],
  );

  const switchMode = useCallback(
    (newMode: ExecutorMode) => {
      if (newMode === "docker") {
        setValue(`${name}.${index}.npx`, undefined);
        setValue(`${name}.${index}.args`, undefined);
        setValue(`${name}.${index}.docker`, DEFAULT_DOCKER_IMAGE);
      } else {
        setValue(`${name}.${index}.docker`, undefined);
        setValue(`${name}.${index}.npx`, DEFAULT_NPX_PACKAGE);
        setValue(`${name}.${index}.args`, DEFAULT_NPX_ARGS);
      }
    },
    [setValue, name, index],
  );

  return (
    <div className="flex flex-row items-center gap-2">
      <div className="flex-1 flex flex-col gap-y-1 px-2 py-1 border rounded-md">
        <FormField
          control={control}
          name={`${name}.${index}.id`}
          render={({ field }) => (
            <FormItem className="flex flex-row items-center">
              <FormLabel className="w-24">Script ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="script id"
                  {...field}
                  value={field.value}
                  className="h-8 w-[200px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-row items-center">
          <FormLabel className="w-24">Executor</FormLabel>
          <div className="flex flex-row items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="Docker image"
              onClick={() => switchMode("docker")}
              className={cn(
                "h-8 gap-1.5 px-2",
                mode === "docker" && "bg-accent",
              )}
            >
              <DockerIcon className="h-4 w-4" />
              Docker
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              title="NPM package"
              onClick={() => switchMode("npm")}
              className={cn("h-8 gap-1.5 px-2", mode === "npm" && "bg-accent")}
            >
              <NpmIcon className="h-4 w-4" />
              npm
            </Button>
          </div>
        </div>

        {mode === "docker" ? (
          <FormField
            control={control}
            name={`${name}.${index}.docker`}
            render={({ field }) => <ScriptDockerLite field={field} />}
          />
        ) : (
          <FormField
            control={control}
            name={`${name}.${index}.npx`}
            render={({ field }) => <ScriptNpxLite field={field} />}
          />
        )}

        <FormField
          key={mode}
          control={control}
          name={`${name}.${index}.args`}
          render={({ field }) => <ScriptArgsLite field={field} />}
        />

        <div className="self-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggleExpanded}
          >
            {isExpanded ? "...show less" : "...show more"}
          </Button>
        </div>

        {isExpanded && (
          <>
            <div className="flex flex-row items-center">
              <Label className="w-24">Flags</Label>
              <div className="flex flex-row items-center gap-x-6">
                {SCRIPT_FLAGS.map(({ value, label }) => (
                  <Label
                    key={value}
                    className="flex flex-row items-center gap-x-2"
                  >
                    <Checkbox
                      checked={flags?.includes(value) ?? false}
                      onCheckedChange={checked =>
                        toggleFlag(value, checked === true)
                      }
                    />
                    {label}
                  </Label>
                ))}
              </div>
            </div>

            <FormField
              control={control}
              name={`${name}.${index}.env`}
              render={({ field }) => <ScriptEnv field={field} />}
            />
          </>
        )}
      </div>

      <div className="self-stretch pt-2 flex flex-col items-center justify-between">
        <div onPointerDown={onStartDrag} className="cursor-grab">
          <DragHandle />
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => removeScript(index)}
        >
          <Trash />
        </Button>
      </div>
    </div>
  );
};

export default ScriptControlLite;
